import {
  type Branch,
  PERMISSIONS,
  RECEIPT_STATUS_LABELS,
  type ReceiptSummary,
  type SearchResponse,
  type SignatureFilter,
} from '@edms/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { FileSearch, Search, SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/page-header';
import { type Column, DataTable, Pagination } from '@/components/ui/data-table';
import { Button, Field, Input, Select } from '@/components/ui/primitives';
import { Badge, EmptyState, type Tone } from '@/components/ui/surfaces';
import { useAuth } from '@/features/auth/auth-context';
import { DocumentViewer } from '@/features/receipts/document-viewer';
import { api } from '@/lib/api-client';
import { cn, formatDate, formatNumber, formatPercent } from '@/lib/utils';

interface Filters {
  orderNumber: string;
  branchId: string;
  dateFrom: string;
  dateTo: string;
  customerName: string;
  driverName: string;
  signature: SignatureFilter;
}

const EMPTY_FILTERS: Filters = {
  orderNumber: '',
  branchId: '',
  dateFrom: '',
  dateTo: '',
  customerName: '',
  driverName: '',
  signature: 'ANY',
};

const STATUS_TONES: Record<ReceiptSummary['status'], Tone> = {
  IMPORTED: 'neutral',
  PROCESSING: 'info',
  OCR_PENDING: 'info',
  NEEDS_REVIEW: 'warning',
  DUPLICATE_SUSPECTED: 'warning',
  READY: 'accent',
  ARCHIVED: 'success',
  REJECTED: 'danger',
};

/**
 * Recherche documentaire (§10).
 *
 * Le champ « numéro de commande » est le seul mis en avant : c'est la recherche
 * décrite comme principale, et 95 % des usages s'y limitent. Les critères
 * avancés existent mais restent repliés — les afficher en permanence
 * ralentirait le cas courant sans servir le cas rare.
 *
 * La saisie est temporisée puis envoyée automatiquement : aucun bouton
 * « Rechercher » à cliquer, conformément à l'exigence de réduction du nombre de
 * clics (§19).
 */
export function SearchPage(): React.JSX.Element {
  const { receiptId } = useParams<{ receiptId?: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState<Filters>(EMPTY_FILTERS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data: branches } = useQuery({
    queryKey: ['branches', 'selectable'],
    queryFn: () => api.get<Branch[]>('/branches/selectable'),
    staleTime: 5 * 60_000,
  });

  // Temporisation : on interroge le serveur quand l'utilisateur cesse de taper.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(1);
    }, 280);
    return () => clearTimeout(timer);
  }, [filters]);

  // Raccourci « / » : place le curseur dans la recherche depuis n'importe où.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey) return;
      const target = event.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      event.preventDefault();
      searchInputRef.current?.focus();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const query = useMemo(
    () => ({
      page,
      pageSize,
      orderNumber: debouncedFilters.orderNumber || undefined,
      branchId: debouncedFilters.branchId || undefined,
      dateFrom: debouncedFilters.dateFrom || undefined,
      dateTo: debouncedFilters.dateTo || undefined,
      customerName: debouncedFilters.customerName || undefined,
      driverName: debouncedFilters.driverName || undefined,
      signature: debouncedFilters.signature,
    }),
    [debouncedFilters, page, pageSize],
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['receipts', 'search', query],
    queryFn: () => api.get<SearchResponse>('/receipts/search', query),
    // Conserver le résultat précédent pendant le chargement évite que le
    // tableau clignote à chaque frappe.
    placeholderData: keepPreviousData,
  });

  const activeFilterCount = Object.entries(debouncedFilters).filter(
    ([key, value]) => key !== 'orderNumber' && value !== '' && value !== 'ANY',
  ).length;

  const voitLaQualiteOcr = can(PERMISSIONS.RECEIPTS_CORRECT);

  const columns: readonly Column<ReceiptSummary>[] = [
    {
      key: 'orderNumber',
      header: 'Numéro de commande',
      width: '18%',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-mono text-sm font-medium text-ink">
            {row.orderNumber ?? row.searchKey}
          </span>
          {row.subNumber && row.subNumber !== row.orderNumber && (
            <span className="font-mono text-2xs text-ink-faint">sous-n° {row.subNumber}</span>
          )}
        </div>
      ),
    },
    {
      key: 'deliveryDate',
      header: 'Livraison',
      width: '10%',
      render: (row) => <span className="text-sm text-ink-muted">{formatDate(row.deliveryDate)}</span>,
    },
    {
      key: 'branch',
      header: 'Bureau',
      width: '14%',
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-2xs text-ink-faint">{row.branchCode}</span>
          <span className="truncate text-sm text-ink-muted">{row.branchName}</span>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Client',
      hideOnNarrow: true,
      render: (row) => <span className="truncate text-sm text-ink-muted">{row.customerName ?? '—'}</span>,
    },
    {
      key: 'driver',
      header: 'Chauffeur',
      hideOnNarrow: true,
      render: (row) => <span className="truncate text-sm text-ink-muted">{row.driverName ?? '—'}</span>,
    },
    {
      key: 'signature',
      header: 'Signature',
      align: 'center',
      width: '8%',
      render: (row) =>
        row.hasSignature ? (
          <Badge tone="success">Oui</Badge>
        ) : (
          <span className="text-xs text-ink-faint">Non</span>
        ),
    },
    // Colonne réservée à ceux qui corrigent : le taux de reconnaissance sert à
    // décider quoi relire avant validation. Un compte de consultation n'a rien
    // à en faire, et un pourcentage inférieur à 100 % sur un document exact
    // ferait douter de l'archive.
    ...(voitLaQualiteOcr
      ? [
          {
            key: 'confidence',
            header: 'OCR',
            align: 'right',
            width: '7%',
            hideOnNarrow: true,
            render: (row: ReceiptSummary) => (
              <span className="text-xs text-ink-muted">{formatPercent(row.ocrConfidence)}</span>
            ),
          } satisfies Column<ReceiptSummary>,
        ]
      : []),
    {
      key: 'status',
      header: 'Statut',
      align: 'right',
      width: '10%',
      render: (row) => <Badge tone={STATUS_TONES[row.status]}>{RECEIPT_STATUS_LABELS[row.status]}</Badge>,
    },
  ];

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <PageHeader
          title="Recherche documentaire"
          description="Retrouvez un reçu par son numéro de commande ou affinez par critères"
          toolbar={
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-64 flex-1">
                  <Input
                    ref={searchInputRef}
                    size="lg"
                    value={filters.orderNumber}
                    onChange={(event) =>
                      setFilters((current) => ({ ...current, orderNumber: event.target.value }))
                    }
                    placeholder="Numéro de commande ou sous-numéro…   (touche /)"
                    leadingIcon={<Search className="size-4" />}
                    className="font-mono"
                    aria-label="Numéro de commande"
                    trailingSlot={
                      filters.orderNumber ? (
                        <button
                          type="button"
                          onClick={() => setFilters((current) => ({ ...current, orderNumber: '' }))}
                          className="rounded p-1 text-ink-faint hover:text-ink"
                          aria-label="Effacer la recherche"
                        >
                          <X className="size-4" />
                        </button>
                      ) : undefined
                    }
                  />
                </div>

                <Button
                  size="lg"
                  variant={showAdvanced ? 'subtle' : 'secondary'}
                  icon={<SlidersHorizontal className="size-4" />}
                  onClick={() => setShowAdvanced((open) => !open)}
                >
                  Recherche avancée
                  {activeFilterCount > 0 && (
                    <Badge tone="accent" className="ml-1">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>

                {(activeFilterCount > 0 || filters.orderNumber) && (
                  <Button size="lg" variant="ghost" onClick={() => setFilters(EMPTY_FILTERS)}>
                    Réinitialiser
                  </Button>
                )}
              </div>

              {showAdvanced && (
                <div className="anim-in grid gap-3 rounded-lg border border-line-subtle bg-raised p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                  <Field label="Bureau" htmlFor="filter-branch">
                    <Select
                      id="filter-branch"
                      size="sm"
                      value={filters.branchId}
                      onChange={(event) =>
                        setFilters((current) => ({ ...current, branchId: event.target.value }))
                      }
                    >
                      <option value="">Tous les bureaux</option>
                      {branches?.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.code} — {branch.name}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field label="Livré à partir du" htmlFor="filter-from">
                    <Input
                      id="filter-from"
                      size="sm"
                      type="date"
                      value={filters.dateFrom}
                      onChange={(event) =>
                        setFilters((current) => ({ ...current, dateFrom: event.target.value }))
                      }
                    />
                  </Field>

                  <Field label="Jusqu'au" htmlFor="filter-to">
                    <Input
                      id="filter-to"
                      size="sm"
                      type="date"
                      value={filters.dateTo}
                      onChange={(event) =>
                        setFilters((current) => ({ ...current, dateTo: event.target.value }))
                      }
                    />
                  </Field>

                  <Field label="Client" htmlFor="filter-customer">
                    <Input
                      id="filter-customer"
                      size="sm"
                      value={filters.customerName}
                      onChange={(event) =>
                        setFilters((current) => ({ ...current, customerName: event.target.value }))
                      }
                      placeholder="Nom du client"
                    />
                  </Field>

                  <Field label="Chauffeur" htmlFor="filter-driver">
                    <Input
                      id="filter-driver"
                      size="sm"
                      value={filters.driverName}
                      onChange={(event) =>
                        setFilters((current) => ({ ...current, driverName: event.target.value }))
                      }
                      placeholder="Nom du chauffeur"
                    />
                  </Field>

                  <Field label="Signature" htmlFor="filter-signature">
                    <Select
                      id="filter-signature"
                      size="sm"
                      value={filters.signature}
                      onChange={(event) =>
                        setFilters((current) => ({
                          ...current,
                          signature: event.target.value as SignatureFilter,
                        }))
                      }
                    >
                      <option value="ANY">Indifférent</option>
                      <option value="PRESENT">Présente</option>
                      <option value="ABSENT">Absente</option>
                    </Select>
                  </Field>
                </div>
              )}
            </div>
          }
        />

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <div className="overflow-hidden rounded-lg border border-line-subtle bg-raised shadow-[var(--shadow-card)]">
            <div className="flex items-center justify-between border-b border-line-subtle px-4 py-2.5">
              <p className="text-xs text-ink-faint">
                {data ? (
                  <>
                    <span className="tabular font-medium text-ink-muted">
                      {formatNumber(data.total)}
                    </span>{' '}
                    document{data.total > 1 ? 's' : ''}
                    {/* Le temps serveur est affiché : l'exigence des deux
                        secondes du §10 doit être vérifiable, pas seulement
                        affirmée. */}
                    <span className="tabular ml-2 text-ink-faint">
                      · {data.elapsedMs.toFixed(0)} ms
                    </span>
                  </>
                ) : (
                  'Recherche…'
                )}
              </p>
              {isFetching && !isLoading && (
                <span className="text-2xs text-ink-faint">Actualisation…</span>
              )}
            </div>

            <DataTable
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(row) => row.id}
              loading={isLoading}
              activeRowKey={receiptId ?? null}
              onRowClick={(row) => navigate(`/recherche/${row.id}`)}
              empty={
                <EmptyState
                  icon={FileSearch}
                  title={
                    filters.orderNumber || activeFilterCount > 0
                      ? 'Aucun document ne correspond'
                      : 'Saisissez un numéro de commande'
                  }
                  description={
                    filters.orderNumber || activeFilterCount > 0
                      ? "Vérifiez le numéro saisi ou élargissez la période. Un reçu n'apparaît ici qu'une fois archivé."
                      : 'La recherche porte sur les documents archivés de votre périmètre.'
                  }
                />
              }
            />

            {data && (
              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                totalPages={data.totalPages}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {receiptId && (
        // Sur grand écran, la visionneuse s'installe à côté de la liste — on
        // garde le contexte de recherche sous les yeux. En dessous, elle passe
        // en plein écran : la masquer laisserait l'utilisateur devant un clic
        // sans effet.
        <div
          className={cn(
            'anim-in fixed inset-0 z-40 bg-canvas',
            'xl:static xl:z-auto xl:w-[34rem] xl:shrink-0',
          )}
        >
          <DocumentViewer receiptId={receiptId} onClose={() => navigate('/recherche')} />
        </div>
      )}
    </div>
  );
}
